import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, DatePicker, Radio, Button, Popover, message } from 'antd';
import FormValidationHelper from 'utils/validation';
import FileUpload from 'components/form/FileUpload';
import LicenseImg from 'components/LicenseImg';
import s from './s.module.less';
import { FormPhysician } from 'types/form';
import dayjs from 'dayjs';
import { formatDate } from 'utils/common';
import StateSelect from 'components/form/StateSelect';
import { editCollaboratingPhysician } from 'api/newUniprofile';
import { Physician } from 'types/common';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import { sortByProperty } from 'utils/form';
import { EnumFields } from 'types/enumerationData';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import { isEqual } from 'lodash';

interface Props {
    initValue?: Physician[];
    isEditing?: boolean;
    refresh?: () => void;
    scheduleToken?: string;
}

const CollaboratingPhysician = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refresh, scheduleToken } = props;
    const [form] = Form.useForm<{ physicians: FormPhysician[] }>();
    const [, forceUpdate] = React.useState({});
    const [setting, loading] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');
    const prevInitValueRef = useRef<Physician[]>();

    useEffect(() => {
        if (initValue?.length && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue({
                physicians: initValue.map((item) => ({
                    ...item,
                    birthdayDate: item.birthdayDate ? dayjs(item.birthdayDate) : undefined,
                    effectiveDate: item.effectiveDate ? dayjs(item.effectiveDate) : undefined,
                    expDate: item.expDate ? dayjs(item.expDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const data = await form.validateFields();
            const res = await editCollaboratingPhysician(
                data.physicians.map((item) => ({
                    ...item,
                    birthdayDate: item.birthdayDate?.format('YYYY-MM-DD') ?? '',
                    effectiveDate: item.effectiveDate?.format('YYYY-MM-DD') ?? '',
                    expDate: item.expDate?.format('YYYY-MM-DD') ?? '',
                    email: item.email || '',
                })),
                scheduleToken,
            );
            if (res.error) {
                message.error(res.error);
            } else {
                message.success('Collaborating physician information has been updated');
                refresh?.();
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => form.validateFields(),
    }));

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form form={form} initialValues={{ remember: true }} autoComplete="off" layout="vertical">
                    <Form.List name="physicians" initialValue={[{}]}>
                        {(fields, { add, remove }) => {
                            const { length } = fields;
                            return (
                                <div id="physicians">
                                    {fields.map((field, index) => {
                                        const showMore = form.getFieldValue(['physicians', field.name, 'choice']) === 3;
                                        const currentBitrhday = form.getFieldValue([
                                            'physicians',
                                            field.name,
                                            'birthdayDate',
                                        ]);
                                        return (
                                            <Form.Item key={field.key} noStyle>
                                                <div className={s.subModule}>
                                                    <Form.Item
                                                        style={{  marginBottom: 12 }}
                                                        label="State"
                                                        name={[field.name, 'state']}
                                                        validateFirst
                                                        rules={[
                                                            { required: true, message: 'State is a required field' },
                                                        ]}
                                                    >
                                                        <StateSelect />
                                                    </Form.Item>
                                                    <Form.Item
                                                        style={{ margin: 0 }}
                                                        label=""
                                                        name={[field.name, 'choice']}
                                                    >
                                                        <Radio.Group
                                                            onChange={() => {
                                                                forceUpdate({});
                                                            }}
                                                        >
                                                            <Radio value={3}>
                                                                Yes, I have a collaborating physician.
                                                            </Radio>
                                                            <Radio value={1} style={{ marginTop: 4 }}>
                                                                No, it’s not requried in this state.
                                                            </Radio>
                                                            {/* <Radio value={2}>No, I will need a collabrating physician in the future.</Radio> */}
                                                        </Radio.Group>
                                                    </Form.Item>
                                                    {showMore && (
                                                        <div className={s.addModle} style={{ marginTop: 24 }}>
                                                            <h4>
                                                                Please add information of your collaborating physician:
                                                            </h4>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item
                                                                        
                                                                        label="Physician’s name"
                                                                        name={[field.name, 'name']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Physician name is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <Input placeholder="Collaborating physician’s name" />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                    
                                                                        label="Practice phone number"
                                                                        name={[field.name, 'tel']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message:
                                                                                    'Practice phone number is required',
                                                                            },
                                                                            FormValidationHelper.validatePhoneNumber(
                                                                                'Phone number must contain only number or -',
                                                                            ),
                                                                        ]}
                                                                    >
                                                                        <Input maxLength={30} />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                    
                                                                        label="Physician’s email"
                                                                        name={[field.name, 'email']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message:
                                                                                    'Physician’s email is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                        
                                                                        label="Physician’s address"
                                                                        name={[field.name, 'address']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Address is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <AutoAddressInput
                                                                            addCityToAddress
                                                                            onStateChange={(val) => {
                                                                                const targetState = stateOption.find(
                                                                                    (item) =>
                                                                                        item.value === val ||
                                                                                        item.label === val,
                                                                                );
                                                                                if (targetState) {
                                                                                    form.setFieldsValue({
                                                                                        physicians: form
                                                                                            .getFieldValue('physicians')
                                                                                            .map(
                                                                                                (
                                                                                                    item: any,
                                                                                                    idx: number,
                                                                                                ) => {
                                                                                                    if (
                                                                                                        idx ===
                                                                                                        field.name
                                                                                                    ) {
                                                                                                        return {
                                                                                                            ...item,
                                                                                                            physicianState:
                                                                                                                targetState.value,
                                                                                                        };
                                                                                                    }
                                                                                                    return item;
                                                                                                },
                                                                                            ),
                                                                                    });
                                                                                }
                                                                            }}
                                                                            onPostcodeChange={(val) => {
                                                                                if (val) {
                                                                                    form.setFieldsValue({
                                                                                        physicians: form
                                                                                            .getFieldValue('physicians')
                                                                                            .map(
                                                                                                (
                                                                                                    item: any,
                                                                                                    idx: number,
                                                                                                ) => {
                                                                                                    if (
                                                                                                        idx ===
                                                                                                        field.name
                                                                                                    ) {
                                                                                                        return {
                                                                                                            ...item,
                                                                                                            zip: val,
                                                                                                        };
                                                                                                    }
                                                                                                    return item;
                                                                                                },
                                                                                            ),
                                                                                    });
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                            
                                                                        label="State"
                                                                        name={[field.name, 'physicianState']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'State is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <StateSelect />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                      
                                                                        label="ZIP code"
                                                                        name={[field.name, 'zip']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'ZIP code is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                      
                                                                        label="Date of Birth"
                                                                        name={[field.name, 'birthdayDate']}
                                                                    >
                                                                        <DatePicker
                                                                            disabledDate={(current) => {
                                                                                const day18Ago = dayjs().subtract(
                                                                                    18,
                                                                                    'year',
                                                                                );
                                                                                return (
                                                                                    current.valueOf() >
                                                                                    day18Ago.valueOf()
                                                                                );
                                                                            }}
                                                                            defaultPickerValue={
                                                                                currentBitrhday
                                                                                    ? undefined
                                                                                    : dayjs().subtract(18, 'year')
                                                                            }
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                       
                                                                        label="Physician’s NPI number"
                                                                        name={[field.name, 'npi']}
                                                                        validateFirst
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'NPI is required',
                                                                            },
                                                                            FormValidationHelper.validateLicenseNumber(
                                                                                'The NPI must contain only letters or numbers',
                                                                            ),
                                                                        ]}
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                 
                                                                        label="Licensed states"
                                                                        name={[field.name, 'licenseState']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'License state is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <StateSelect />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                              
                                                                        label="Physician’s specialities"
                                                                        name={[field.name, 'specialities']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Specialities is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <Input placeholder="Specialties and separate with “,”" />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                              
                                                                        label="Effective date of the agreement"
                                                                        name={[field.name, 'effectiveDate']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Effective date is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <DatePicker style={{ width: '100%' }} />
                                                                    </Form.Item>
                                                                </div>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                         
                                                                        label="Expiration date of the agreement"
                                                                        name={[field.name, 'expDate']}
                                                                    >
                                                                        <DatePicker
                                                                            disabledDate={(date) => {
                                                                                const now = new Date().getTime();
                                                                                if (!date) {
                                                                                    return false;
                                                                                }
                                                                                return date?.valueOf() < now;
                                                                            }}
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item
                                                                        label="Agreement upload"
                                                                        name={[field.name, 'url']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Agreement is required',
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <FileUploadPrivate uploadType="LICENSE" />
                                                                        {/* <FileUpload /> */}
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <div className={s.row}>
                                                                <div className={s.rowItem}>
                                                                    <Form.Item                                                                       
                                                                        label={
                                                                            <div className={s.labelTitle}>
                                                                                Collaborating physician’s DEA number
                                                                                <Popover
                                                                                    placement="bottomLeft"
                                                                                    content={
                                                                                        <div className={s.toolWrap}>
                                                                                            A DEA number is required for
                                                                                            your collaborating physician
                                                                                            to prescribe controlled
                                                                                            medicines in Kiwi EHR when
                                                                                            you purchase e-Prescription
                                                                                            + EPCS for them. You can add
                                                                                            the DEA number here or later
                                                                                            in your UniProfile under
                                                                                            Collaborating Physicians.
                                                                                        </div>
                                                                                    }
                                                                                    trigger="hover"
                                                                                >
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        width="17"
                                                                                        height="17"
                                                                                        viewBox="0 0 17 17"
                                                                                        fill="none"
                                                                                    >
                                                                                        <path
                                                                                            d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                                                                            stroke="#6B7280"
                                                                                            strokeWidth="1.33594"
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                    </svg>
                                                                                </Popover>
                                                                            </div>
                                                                        }
                                                                        name={[field.name, 'deaNumber']}
                                                                        validateFirst
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {field.name !== 0 && (
                                                        <div className="mt-6">
                                                            <Button onClick={() => remove(index)} danger>
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                                {field.name === length - 1 && (
                                                    <div
                                                        onClick={() => {
                                                            add();
                                                            const wrap = document.getElementById('physicians');
                                                            setTimeout(() => {
                                                                wrap?.parentElement?.parentElement?.scroll({
                                                                    top: 1000000,
                                                                    behavior: 'smooth',
                                                                });
                                                            }, 200);
                                                        }}
                                                        className={s.add}
                                                    >
                                                        + Collaborating physician
                                                    </div>
                                                )}
                                            </Form.Item>
                                        );
                                    })}
                                </div>
                            );
                        }}
                    </Form.List>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        const physicianArray: Physician[] = initValue?.filter((item: Physician) => !!(item && item.state)) ?? [];
        if (!physicianArray?.length) {
            return <div>Not provided</div>;
        }
        return (
            <>
                {physicianArray?.map((item: Physician) => {
                    return (
                        <div className={s.subModule} key={`${item.state}_${item.choice}`}>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>State</div>
                                    <div className={s.value}>{item.state}</div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div className={s.displayItem}>
                                    <div className={s.label}>Do you have a collaborating physician now?</div>
                                    <div className={s.value}>
                                        {`${item.choice}` === '1' && 'No, it’s not required in this state.'}
                                        {`${item.choice}` === '2' &&
                                            'No, I will need a collabrating physician in the future.'}
                                        {`${item.choice}` === '3' && 'Yes, I have a collaborating physician'}
                                    </div>
                                </div>
                            </div>
                            {`${item.choice}` === '3' && (
                                <>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Physician’s name</div>
                                            <div className={s.value}>{item.name}</div>
                                        </div>

                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Personal phone number</div>
                                            <div className={s.value}>{item.tel}</div>
                                        </div>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Email</div>
                                            <div className={s.value}>{item.email}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Physician’s address</div>
                                            <div className={s.value}>{item.address}</div>
                                        </div>

                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>ZIP code</div>
                                            <div className={s.value}>{item.zip}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Date of Birth</div>
                                            <div className={s.value}>
                                                {item.birthdayDate ? formatDate(dayjs(item.birthdayDate)) : '-'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Physician’s NPI number</div>
                                            <div className={s.value}>{item.npi}</div>
                                        </div>

                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Licensed states</div>
                                            <div className={s.value}>{item.licenseState}</div>
                                        </div>
                                        <div
                                            style={{
                                                width: '180px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Physician’s specialities</div>
                                            <div className={s.value}>{item.specialities}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Effective date of the agreement</div>
                                            <div className={s.value}>
                                                {item.effectiveDate ? formatDate(dayjs(item.effectiveDate)) : '-'}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                width: '300px',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Expiration date of the agreement</div>
                                            <div className={s.value}>
                                                {item.expDate ? formatDate(dayjs(item.expDate)) : '-'}{' '}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '80%',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>Uploaded license</div>
                                            <div className={s.value}>
                                                <LicenseImg src={item.fullUrl || item.url} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div
                                            style={{
                                                width: '80%',
                                            }}
                                            className={s.displayItem}
                                        >
                                            <div className={s.label}>
                                                Collaborating physician’s DEA number
                                                <Popover
                                                    placement="bottomLeft"
                                                    content={
                                                        <div className={s.toolWrap}>
                                                            A DEA number is required for your collaborating physician to
                                                            prescribe controlled medicines in Kiwi EHR when you purchase
                                                            e-Prescription + EPCS for them. You can add the DEA number
                                                            here or later in your UniProfile under Collaborating
                                                            Physicians.
                                                        </div>
                                                    }
                                                    trigger="hover"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="17"
                                                        height="17"
                                                        viewBox="0 0 17 17"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                                            stroke="#6B7280"
                                                            strokeWidth="1.33594"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </Popover>
                                            </div>
                                            <div className={s.value}>{item.deaNumber || '-'}</div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    return <div>{isEditing ? renderEditing() : renderReview()}</div>;
});

export default CollaboratingPhysician;
